/**
 * Theme Switcher — VH-133
 *
 * Three explicit choices rather than a two-state switch. "System" is a real
 * preference, not the absence of one: it means keep following the operating
 * system, which a toggle cannot express and, once left, could never be
 * returned to.
 *
 * Sits beside LanguageSwitcher in the top app bar and deliberately mirrors its
 * shape — same Menu, same transition, same button metrics — so the two read as
 * one pair of controls rather than two designs.
 */
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import {
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { useTheme, THEMES } from '../hooks/useTheme'

const ICONS = {
  light: SunIcon,
  dark: MoonIcon,
  system: ComputerDesktopIcon,
}

export default function ThemeToggle() {
  const { t } = useTranslation()
  const { preference, theme, setTheme } = useTheme()

  // The button shows what you are *looking at*, not what you chose: under
  // "system" the useful information is which palette is currently rendering.
  const CurrentIcon = ICONS[theme] ?? SunIcon

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
        aria-label={t('theme.switchTheme')}
      >
        <CurrentIcon className="h-5 w-5" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-surface-container shadow-lg ring-1 ring-outline-variant focus:outline-none z-50">
          <div className="py-1">
            {THEMES.map((option) => {
              const Icon = ICONS[option]
              return (
                <Menu.Item key={option}>
                  {({ active }) => (
                    <button
                      onClick={() => setTheme(option)}
                      className={clsx(
                        'flex w-full items-center justify-between px-4 py-2 text-sm',
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                        preference === option && 'font-semibold'
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-gray-500" />
                        <span>{t(`theme.${option}`)}</span>
                      </span>
                      {preference === option && (
                        <CheckIcon className="h-4 w-4 text-primary-600" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              )
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
